import { useContext } from 'react';
import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  IconButton,
  Text,
  useColorMode,
  useColorModeValue,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { HamburgerIcon } from '@chakra-ui/icons';
import { logEvent } from 'firebase/analytics';
import { useNavigate } from 'react-router-dom';
import { useAuthModal } from '@/hooks';
import { useUserProfile } from '@/session/useUserProfile';
import { hasPasswordProvider } from '@/helpers';
import { FirebaseContext } from '@/session/FirebaseContext';
import { siteNavAccountButtonProps } from './siteNavLayout';
import { SiteNavLinkLabel } from './SiteNavLinkLabel';
import { useSiteNavNavigation } from './useSiteNavNavigation';

export function SiteNavMobileMenu(props: {
  /** When true, show hamburger (mobile or desktop overflow). */
  compact: boolean;
  openEditName?: () => void;
  openDeleteAccount?: () => void;
  openSetPassword?: () => void;
  openChangePassword?: () => void;
}) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { routes, isActive, onNavClick, activeColor, inactiveColor, hoverBg, activeBg } =
    useSiteNavNavigation();
  const emphasizeColor = useColorModeValue('brandAccent', 'babyBlue');
  const { openLoginModal, openRegisterModal } = useAuthModal();
  const ctx = useContext(FirebaseContext);
  const userData = useUserProfile().userData;
  const navigate = useNavigate();
  const { colorMode } = useColorMode();

  const closeThen = (fn: () => void) => {
    onClose();
    fn();
  };

  const signOut = async () => {
    onClose();
    await ctx.auth.signOut();
    logEvent(ctx.analytics, 'log_out');
    navigate('/rules', { replace: true });
  };

  return (
    <>
      {props.compact ? (
        <IconButton
          aria-label='Open menu'
          variant='ghost'
          size='sm'
          icon={<HamburgerIcon boxSize={5} />}
          onClick={onOpen}
        />
      ) : null}
      <Drawer isOpen={isOpen} placement='right' onClose={onClose} size='xs'>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton mt={1} />
          <DrawerHeader pt={6} pb={2} fontSize='md'>
            Menu
          </DrawerHeader>
          <DrawerBody
            display='flex'
            flexDirection='column'
            px={4}
            pb='calc(1.5rem + env(safe-area-inset-bottom, 0px))'
          >
            <VStack align='stretch' spacing={1} flex={1}>
              {routes.map(({ label, path, badge, emphasize }) => {
                const active = isActive(path);
                return (
                  <Button
                    key={path}
                    variant='ghost'
                    justifyContent='flex-start'
                    w='full'
                    h={12}
                    fontSize='md'
                    fontWeight={active || emphasize ? 700 : 500}
                    color={active ? activeColor : emphasize ? emphasizeColor : inactiveColor}
                    bg={active ? activeBg : undefined}
                    _hover={{ bg: active ? activeBg : hoverBg }}
                    onClick={() => onNavClick(path, onClose)}
                  >
                    <SiteNavLinkLabel label={label} badge={badge} />
                  </Button>
                );
              })}
            </VStack>

            <Box pt={4} mt={2} borderTopWidth='1px' borderColor='chakra-border-color'>
              {ctx.user ? (
                <VStack align='stretch' spacing={1}>
                  <Text px={3} py={2} fontSize='sm' fontWeight='semibold' noOfLines={1} color={inactiveColor}>
                    {userData?.name || 'Account'}
                  </Text>
                  {userData?.admin ? (
                    <Button
                      variant='ghost'
                      justifyContent='flex-start'
                      w='full'
                      h={12}
                      fontSize='md'
                      onClick={() => closeThen(() => navigate('/admin', { replace: true }))}
                    >
                      Admin Console
                    </Button>
                  ) : null}
                  <Button
                    variant='ghost'
                    justifyContent='flex-start'
                    w='full'
                    h={12}
                    fontSize='md'
                    onClick={() => closeThen(() => props.openEditName?.())}
                  >
                    Edit Name
                  </Button>
                  {ctx.user && !hasPasswordProvider(ctx.user) ? (
                    <Button
                      variant='ghost'
                      justifyContent='flex-start'
                      w='full'
                      h={12}
                      fontSize='md'
                      onClick={() => closeThen(() => props.openSetPassword?.())}
                    >
                      Set password
                    </Button>
                  ) : null}
                  {ctx.user && hasPasswordProvider(ctx.user) ? (
                    <Button
                      variant='ghost'
                      justifyContent='flex-start'
                      w='full'
                      h={12}
                      fontSize='md'
                      onClick={() => closeThen(() => props.openChangePassword?.())}
                    >
                      Change password
                    </Button>
                  ) : null}
                  <Button
                    variant='ghost'
                    justifyContent='flex-start'
                    w='full'
                    h={12}
                    fontSize='md'
                    onClick={() => void signOut()}
                  >
                    Sign Out
                  </Button>
                  <Button
                    variant='ghost'
                    justifyContent='flex-start'
                    w='full'
                    h={12}
                    fontSize='md'
                    color={colorMode === 'light' ? 'red.500' : 'red.300'}
                    onClick={() => closeThen(() => props.openDeleteAccount?.())}
                  >
                    Delete Account
                  </Button>
                </VStack>
              ) : (
                <VStack align='stretch' spacing={2} pt={2}>
                  <Button
                    {...siteNavAccountButtonProps}
                    variant='ghost'
                    w='full'
                    h={12}
                    fontSize='md'
                    onClick={() => closeThen(openLoginModal)}
                  >
                    Log In
                  </Button>
                  <Button
                    {...siteNavAccountButtonProps}
                    colorScheme='green'
                    w='full'
                    h={12}
                    fontSize='md'
                    onClick={() => closeThen(openRegisterModal)}
                  >
                    Sign Up
                  </Button>
                </VStack>
              )}
            </Box>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
}
